import { ArrowLeft, Heart, Mail, Send, Shield } from "lucide-react";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import LanguageSwitcher from "../../components/ui/LanguageSwitcher";
import { useI18nStore } from "../../stores/i18nStore";

export const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [error, setError] = useState("");

  const { t } = useI18nStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const apiUrl = import.meta.env.VITE_API_URL || "/api";
      const response = await fetch(`${apiUrl}/auth/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.error?.message ||
            errorData?.message ||
            t.auth2ResetEmailError,
        );
      }

      setIsEmailSent(true);
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message || t.auth2ResetEmailError);
      } else {
        setError(t.auth2ResetEmailError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isEmailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-emerald-50 flex flex-col justify-center py-8 px-4 sm:px-6 lg:px-8">
        {/* Language Switcher */}
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>

        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          {/* Logo and header */}
          <div className="flex justify-center">
            <div className="bg-gradient-to-r from-primary-600 to-emerald-600 p-3 rounded-2xl shadow-lg">
              <div className="flex items-center space-x-2">
                <Heart className="h-8 w-8 text-white" />
                <Shield className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>

          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
            {t.emailSent}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t.checkYourEmail}
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-6 shadow-xl rounded-xl border border-gray-100">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <Send className="h-8 w-8 text-green-600" />
              </div>

              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t.resetEmailSent}
              </h3>

              <p className="text-sm text-gray-600 mb-6">
                {t.auth2ResetLinkSentTo} <strong>{email}</strong>
              </p>

              <div className="space-y-3">
                <p className="text-xs text-gray-500">
                  {t.auth2NoEmailReceived}
                </p>

                <button
                  onClick={() => setIsEmailSent(false)}
                  className="text-sm text-primary-600 hover:text-primary-500 font-medium"
                >
                  {t.auth2ResendEmail}
                </button>
              </div>
            </div>
          </div>

          {/* Back to sign-in */}
          <div className="mt-6">
            <Link
              to="/traveler/login"
              className="w-full flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t.backToLogin}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-emerald-50 flex flex-col justify-center py-8 px-4 sm:px-6 lg:px-8">
      {/* Language Switcher */}
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo and header */}
        <div className="flex justify-center">
          <div className="bg-gradient-to-r from-primary-600 to-emerald-600 p-3 rounded-2xl shadow-lg">
            <div className="flex items-center space-x-2">
              <Heart className="h-8 w-8 text-white" />
              <Shield className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>

        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
          {t.resetPassword}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {t.resetPasswordDescription}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-6 shadow-xl rounded-xl border border-gray-100">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Email Input */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {t.email}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors duration-200"
                  placeholder={t.auth2EmailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="text-red-800 text-sm text-center">{error}</div>
              </div>
            )}

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-primary-600 to-emerald-600 hover:from-primary-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    <span>{t.loading}</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Send className="h-4 w-4 mr-2" />
                    <span>{t.sendResetLink}</span>
                  </div>
                )}
              </button>
            </div>

            {/* Info Text */}
            <div className="text-center">
              <p className="text-xs text-gray-500">{t.auth2ResetLinkInfo}</p>
            </div>
          </form>
        </div>

        {/* Back to sign-in */}
        <div className="mt-6">
          <Link
            to="/traveler/login"
            className="w-full flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t.backToLogin}
          </Link>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center text-xs text-gray-500">
        <p>
          {t.auth2CopyrightLine.replace(
            "{year}",
            String(new Date().getFullYear()),
          )}
        </p>
      </div>
    </div>
  );
};
